/** SASE-35 (P35c): the customer organisation a partner's person is managing.
 *
 *  While one is chosen, every request to the customer's SASE pages carries
 *  `X-Sase-Customer-Org` (`lib/http.ts`), and the server answers for that
 *  customer — as the person's partner role allows. The partner's own pages
 *  (`/sase/partner…`), the customer's page about its partner
 *  (`/sase/partner-access…`), end users' pages (`/sase/me/…`) and everything
 *  outside SASE never carry it (the server refuses it there too).
 *
 *  Kept for the browser tab only (sessionStorage): a new tab starts as the
 *  person's own organisation. */
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

export const CUSTOMER_ORG_HEADER = "X-Sase-Customer-Org";

export interface ActingFor {
  orgId: string;
  name: string;
  partnerName: string;
  /** What the customer allows: "admin" changes settings, "viewer" reads. */
  role: "admin" | "viewer";
}

interface ActingForState {
  acting: ActingFor | null;
  start: (acting: ActingFor) => void;
  stop: () => void;
}

export const useActingForStore = create<ActingForState>()(
  persist(
    (set) => ({
      acting: null,
      start: (acting) => set({ acting }),
      stop: () => set({ acting: null }),
    }),
    {
      name: "omnicloud-sase-acting-for",
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);

/** Whether a request to `url` is one of the customer's SASE pages. */
export function actsForCustomer(url: string): boolean {
  const path = url
    .replace(/^https?:\/\/[^/]+/, "")
    .replace(/^\/api\/v1/, "")
    .split("?")[0] as string;
  return /^\/sase\//.test(path) && !/^\/sase\/(partner|me\/)/.test(path);
}

/** The customer organisation a request to `url` acts for, if any. */
export function customerOrgFor(url: string): string | null {
  const acting = useActingForStore.getState().acting;
  return acting && actsForCustomer(url) ? acting.orgId : null;
}
